import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Package, Search, Eye, Pencil, Printer, Trash2, Truck, MoreVertical, CheckCircle2, Filter,
} from "lucide-react";
import { toast } from "sonner";
import { useApp, can } from "@/context/AppContext";
import {
  listPackingSlips, dispatchPackingSlip, deletePackingSlip,
} from "@/lib/api";
import { fmtDateTime, todayISO } from "@/lib/format";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const SavedPackingSlips = () => {
  const { currentUser, currentLocation } = useApp();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [dispatchedFilter, setDispatchedFilter] = useState("ALL"); // ALL | open | dispatched
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (currentLocation && currentLocation !== "ALL") params.location_code = currentLocation;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (dispatchedFilter === "open") params.dispatched = "false";
      if (dispatchedFilter === "dispatched") params.dispatched = "true";
      const data = await listPackingSlips(params);
      setList(data);
    } catch {
      toast.error("Failed to load packing slips");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [q, currentLocation, fromDate, toDate, dispatchedFilter]);

  const handleDispatch = async (s) => {
    try {
      await dispatchPackingSlip(s.id, currentUser.name, currentUser.role);
      toast.success(`${s.slip_number} marked as dispatched`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dispatch failed");
    }
  };

  const handleDelete = async (s) => {
    try {
      await deletePackingSlip(s.id, currentUser.name, currentUser.role);
      toast.success(`${s.slip_number} deleted`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const print = (s) =>
    window.open(`/packing-slip/${s.id}/print`, "_blank", "noopener");

  const resetFilters = () => {
    setQ(""); setFromDate(""); setToDate(""); setDispatchedFilter("ALL");
  };

  const canWrite = can(currentUser, "packing_slip_write");

  return (
    <div className="space-y-4" data-testid="saved-packing-slips-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-yamaha-50 flex items-center justify-center">
            <Package className="w-6 h-6 text-yamaha-700" />
          </div>
          <div>
            <h1 className="text-[24px] font-semibold text-yamaha-800 leading-none tracking-tight">
              Saved Packing Slips
            </h1>
            <p className="text-[12.5px] text-[#747684] mt-1">
              YBC dispatch records — view, edit (open slips only), or print.
            </p>
          </div>
        </div>
        {canWrite && (
        <button
          onClick={() => navigate("/packing-slip/new")}
          data-testid="new-slip-btn"
          className="px-4 py-2 rounded-lg bg-yamaha-700 hover:bg-yamaha-800 text-white text-[12.5px] font-semibold"
        >
          + New Packing Slip
        </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#e2e1eb] rounded-xl p-3 flex flex-wrap items-center gap-2.5">
        <div className="flex items-center gap-1.5 text-[#747684]">
          <Filter className="w-3.5 h-3.5" />
          <span className="text-[12px] font-semibold text-[#444653]">Filters:</span>
        </div>
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-3.5 h-3.5 text-[#747684] absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            data-testid="search-input"
            placeholder="Slip #, Invoice #, Part #, Part name..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full pl-8 pr-2 py-1.5 border border-[#e2e1eb] rounded-md text-[12px]"
          />
        </div>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          data-testid="from-date"
          className="px-2 py-1.5 border border-[#e2e1eb] rounded-md text-[12px]"
          title="From date"
        />
        <span className="text-[11px] text-[#747684]">to</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          data-testid="to-date"
          className="px-2 py-1.5 border border-[#e2e1eb] rounded-md text-[12px]"
          title="To date"
        />
        <select
          value={dispatchedFilter}
          onChange={(e) => setDispatchedFilter(e.target.value)}
          data-testid="dispatched-filter"
          className="px-2 py-1.5 border border-[#e2e1eb] rounded-md text-[12px]"
        >
          <option value="ALL">All Statuses</option>
          <option value="open">Open (editable)</option>
          <option value="dispatched">Dispatched</option>
        </select>
        <button
          onClick={resetFilters}
          className="text-[11.5px] text-yamaha-700 hover:text-yamaha-800 font-semibold"
          data-testid="reset-filters"
        >
          Reset
        </button>
        <span className="ml-auto text-[11px] text-[#747684]">
          {loading ? "Loading…" : `${list.length} slip${list.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="slips-table">
          <thead className="bg-[#faf8ff]">
            <tr>
              <Th>Packing Slip No</Th>
              <Th>Invoice No</Th>
              <Th>Location</Th>
              <Th>Date</Th>
              <Th align="right">Total Qty</Th>
              <Th align="right">Boxes</Th>
              <Th>Status</Th>
              <Th align="center">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && !loading && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-[#747684]">
                  No packing slips found. Click <span className="font-semibold text-yamaha-700">"+ New Packing Slip"</span> to create your first one.
                </td>
              </tr>
            )}
            {list.map((s) => (
              <tr key={s.id} className="border-t border-[#e2e1eb] hover:bg-[#faf8ff]">
                <td className="px-3 py-2 font-mono font-semibold text-yamaha-700">{s.slip_number}</td>
                <td className="px-3 py-2 font-mono">{s.invoice_no}</td>
                <td className="px-3 py-2">{s.location_code}</td>
                <td className="px-3 py-2 text-[#444653]">{fmtDateTime(s.created_at)}</td>
                <td className="px-3 py-2 text-right font-semibold">{s.summary?.total_qty || 0}</td>
                <td className="px-3 py-2 text-right">{s.summary?.total_boxes || 0}</td>
                <td className="px-3 py-2">
                  {s.dispatched ? (
                    <span className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
                      <CheckCircle2 className="w-3 h-3" /> Dispatched
                    </span>
                  ) : (
                    <span className="text-[10.5px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">
                      Open
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-center gap-1">
                    <button
                      onClick={() => navigate(`/packing-slip/new?edit=${s.id}`)}
                      data-testid={`view-${s.slip_number}`}
                      className="p-1.5 rounded hover:bg-yamaha-50 text-yamaha-700"
                      title="View"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => print(s)}
                      data-testid={`print-${s.slip_number}`}
                      className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700"
                      title="Print"
                    >
                      <Printer className="w-3.5 h-3.5" />
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        data-testid={`menu-${s.slip_number}`}
                        className="p-1.5 rounded hover:bg-[#f4f3fc] text-[#444653]"
                      >
                        <MoreVertical className="w-3.5 h-3.5" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!s.dispatched && canWrite && (
                          <DropdownMenuItem
                            onClick={() => navigate(`/packing-slip/new?edit=${s.id}`)}
                            data-testid={`edit-${s.slip_number}`}
                          >
                            <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                          </DropdownMenuItem>
                        )}
                        {!s.dispatched && canWrite && (
                          <DropdownMenuItem
                            onClick={() => handleDispatch(s)}
                            data-testid={`dispatch-${s.slip_number}`}
                          >
                            <Truck className="w-3.5 h-3.5 mr-2" /> Mark Dispatched
                          </DropdownMenuItem>
                        )}
                        {!s.dispatched && canWrite && (
                          <DropdownMenuItem
                            onClick={() => setConfirmDelete(s)}
                            data-testid={`delete-${s.slip_number}`}
                            className="text-rose-600"
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                          </DropdownMenuItem>
                        )}
                        {!s.dispatched && !canWrite && (
                          <DropdownMenuItem disabled>
                            <span className="text-[#747684]">View only</span>
                          </DropdownMenuItem>
                        )}
                        {s.dispatched && (
                          <DropdownMenuItem disabled>
                            <span className="text-[#747684]">Locked (dispatched)</span>
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirmDelete?.slip_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this packing slip. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(confirmDelete)}
              data-testid="confirm-delete-yes"
              className="bg-rose-600 hover:bg-rose-700"
            >
              Yes, delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const Th = ({ children, align = "left" }) => (
  <th
    className={`px-3 py-2.5 text-[10.5px] font-bold uppercase tracking-wider text-[#747684] ${
      align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left"
    }`}
  >
    {children}
  </th>
);

export default SavedPackingSlips;
